import { useObserve } from "@legendapp/state/react";
import { Outlet, useRouter, useRouterState } from "@tanstack/react-router";

import { App } from "~/components/app";
import type { BoardViewMode } from "~/pages/board/board-session";
import { GuestBoardLayout } from "~/pages/guest-board.layout";
import { user$ } from "~/stores/auth";

type ShellMode = "app" | "guest" | "bare";

/**
 * Single, persistent host for the authenticated `<App>` shell.
 *
 * Mounted once in `__root.component` (replacing the raw `<Outlet />`), this
 * decider picks the chrome based on the active route matches:
 *
 * - `app` — any `/_app/*` route or an app-mode `/boards/$boardId`. Renders the
 *   `<App>` shell (sidebar, chat panel, content). Because both surfaces resolve
 *   to the *same* `<App>` element position, React keeps `<App>` mounted across
 *   the `/_app/*` ↔ app-mode-board boundary and only swaps the `<Outlet />`
 *   children — no sidebar/chat remount.
 * - `guest` — a read-only (`readonly-member` / `readonly-public`) board. Renders
 *   the lightweight `<GuestBoardLayout>` with no sidebar/chat/Ably.
 * - `bare` — login, signup, onboard, and other top-level routes. Renders the
 *   `<Outlet />` directly with no app chrome.
 *
 * Switching between modes still remounts the branch, but those transitions
 * (login ↔ app, guest ↔ app) are rare and usually a full reload.
 */
export const AppShell = () => {
  const router = useRouter();
  // Session-loss safety net (formerly in `AppLayout`). A no-op unless the
  // better-auth atom flips to null mid-tab (e.g. a 401); when it does we
  // invalidate so the relevant `beforeLoad` re-runs and emits the redirect
  // itself — a concurrent `navigate()` here would race the current commit.
  useObserve(() => {
    if (!user$.id.get()) {
      queueMicrotask(() => router.invalidate());
    }
  });

  const mode = useRouterState({
    select: (s): ShellMode => {
      const board = s.matches.find((m) => m.routeId === "/boards/$boardId");
      if (board) {
        const viewMode = (board.context as { viewMode?: BoardViewMode })
          .viewMode;
        return viewMode === "app" ? "app" : "guest";
      }
      return s.matches.some((m) => m.routeId === "/_app") ? "app" : "bare";
    },
  });

  if (mode === "app") {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex min-h-0 min-w-0 flex-1">
          <App>
            <Outlet />
          </App>
        </div>
      </div>
    );
  }

  if (mode === "guest") {
    return (
      <GuestBoardLayout>
        <Outlet />
      </GuestBoardLayout>
    );
  }

  return <Outlet />;
};
